<template>
  <transition name="fade" appear @after-enter="focusModal">
    <focus-trap :active="true">
      <div
        class="fixed inset-0 w-full h-screen flex justify-center"
        :class="[{
          'k-modal__overlay': variant === 'base',
          'bg-transparent': variant === 'transparent',
        }, isIframe && alignPopUpAtTop ? 'items-start' : 'items-center']"
        :style="styleInline"
      >
      <div :class="isIframe && alignPopUpAtTop ? 'grid grid-rows-2' : ''">
        <div
          role="dialog"
          aria-modal="true"
          :aria-label="ariaModalLabel"
          :aria-describedby="$id('modal')"
          :class="[isIframe ? 'row-start-2' : '', {
            'bg-white-high-emphasis show-modal': variant === 'base',
            'bg-transparent': variant === 'transparent',
          }]"
          class="relative max-w-2xl pt-[35px] pb-[28px] px-[56px] k-modal rounded"
          v-click-outside="handleModalClose"
        >
          <div class="absolute top-0 right-0">
            <div
              @click="handleModalClose"
              @keydown.enter.space.prevent="handleModalClose"
              class="cursor-pointer border-[3px] border-solid p-2"
              aria-label="Close"
              role="button"
              tabIndex="0"
              ref="modalContainerBtn"
            >
              <slot name="button">
                <k-icon name="close" class="k-modal__icon text-black-high-emphasis" />
              </slot>
            </div>
          </div>
          <div :id="$id('modal')" class="k-modal__body">
            <slot name="content"/>
          </div>
        </div>
      </div>
      </div>
    </focus-trap>
  </transition>
</template>

<script lang="ts">
import { defineComponent, PropType, onMounted, onUnmounted, ref, computed } from 'vue'
import { KIcon } from '@/components'
import { FocusTrap } from 'focus-trap-vue'

export default defineComponent({
  name: 'KModal',
  emits: ['close'],
  components: {
    KIcon,
    FocusTrap
  },
  props: {
    variant: {
      type: String as PropType<'transparent' | 'base'>,
      default: 'base'
    },
    ariaModalLabel: {
      type: String,
      default: 'Modal Base'
    },
    styleOveride: {
      type: Boolean,
      default: false
    },
    alignPopUpAtTop: {
      type: Boolean,
      default: false
    }
  },
  setup (props, { emit }) {
    const modalContainerBtn = ref<HTMLElement | null>(null)

    const isIframe = computed(() => {
      try{
        return window.top !== window.self
      } catch{
        return true
      }
    })

    const styleInline = computed(() => {
      return props.styleOveride ? 'z-index: 100' : ''
    })
    
    const focusModal = () => {
      setTimeout(() => {
        if (modalContainerBtn.value) {
          modalContainerBtn.value.focus()
        }
      }, 50)
    }
    onMounted(() => {
      document.body.classList.add('overflow-hidden')
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          handleModalClose()
        }
      }, { once: true })
    })
    onUnmounted(() => {
      document.body.classList.remove('overflow-hidden')
    })
    const handleModalClose = () => {
      emit('close')
    }
    return {
      handleModalClose,
      focusModal,
      modalContainerBtn,
      styleInline,
      isIframe
    }
  }
})
</script>

<style scoped>
.k-modal {
  width: calc(100% - theme("spacing.8"));

  &__body {
    @apply w-full overflow-y-auto;
    max-height: 80vh;
  }

  &__icon {
    width: theme("width.4");
    height: theme("height.4");
  }

  &__overlay {
    background: rgba(0, 0, 0, 0.5);
  }
}

@media (min-width: 420px) {
  .k-modal {
    width: theme("width.modal-desktop");
  }
}
</style>
